import { registerLocaleData } from '@angular/common';
import { provideHttpClient, withInterceptors, withNoXsrfProtection } from '@angular/common/http';
import localeEsAr from '@angular/common/locales/es-AR';
import {
	ApplicationConfig,
	importProvidersFrom,
	inject,
	LOCALE_ID,
	provideAppInitializer,
	provideZoneChangeDetection,
} from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withInMemoryScrolling, withRouterConfig, withViewTransitions } from '@angular/router';
import { JwtModule } from '@auth0/angular-jwt';
import { channelInterceptor } from '@webapp/core/helpers/channel.nterceptor';
import { HttpErrorInterceptor } from '@webapp/core/helpers/error.interceptor';
import { provideNgxMask } from 'ngx-mask';
import { routes } from './app.routes';
import { authInterceptor } from '@webapp/auth/interceptors/auth.interceptor';
import { credentialsInterceptor } from '@webapp/auth/interceptors/credentials.interceptor';
import { xsrfInterceptor } from '@webapp/auth/interceptors/xsrf.interceptor';
import { silentRefresh } from '@webapp/auth/bootstrap/silent-refresh.initializer';
import { AuthService } from '@webapp/auth/services/auth.service';
import { API_CONFIG, APP_VERSION, AUTH_URL, CHANNEL, CHANNEL_QUOTE_ENUM, IS_PROD, PAYMENTS_URL, RECAPTCHA_SITE_KEY } from '@webapp/core/config';
import { provideWebPlatform } from '@webapp/platform';
import { provideRiskPlugins } from '@webapp/risk-core';
import { ALL_RISK_PLUGINS, DEFAULT_PLUGIN } from '@webapp/risks/plugins/all-risk-plugins';
import { provideWebRiskUi } from '@webapp/risks/web';
import { provideQuoterRiskUi } from '@webapp/quoter/config/provide-quoter-risk-ui';
import { environment } from '../environments/environment';
import {timeoutInterceptor} from '@webapp/core/helpers/timeout.interceptor';
registerLocaleData(localeEsAr, 'es-AR');

export const appConfig: ApplicationConfig = {
	providers: [
		provideNgxMask(),
		provideHttpClient(
			withInterceptors([
				channelInterceptor,
				// credentialsInterceptor MUST run before authInterceptor (and every other
				// interceptor that hits the network): it sets withCredentials so cookies
				// (access-token, refresh-token, XSRF-TOKEN) are sent on EVERY request,
				// including the login/refresh/logout calls authInterceptor itself triggers.
				credentialsInterceptor,
				// authInterceptor MUST run before HttpErrorInterceptor: it is outer on the
				// request path so HttpErrorInterceptor (inner on the response path) still
				// catches the 401 of any request left unauthenticated after a failed refresh.
				authInterceptor,
				// xsrfInterceptor only matches the session routes and the api-keys POST/DELETE routes, so its position
				// relative to auth/error handling is low-risk; it sits after authInterceptor
				// (so an already-attached Authorization header is preserved) and before
				// HttpErrorInterceptor (so a synthetic csrfMissing rejection reaches the
				// component via the same error path as a real backend 403).
				xsrfInterceptor,
				HttpErrorInterceptor,
				timeoutInterceptor
			]),
			withNoXsrfProtection()
		),
		provideZoneChangeDetection({ eventCoalescing: true }),
		provideRouter(
			routes,
			withInMemoryScrolling({
				scrollPositionRestoration: 'top',
			}),
			withRouterConfig({
				urlUpdateStrategy: 'deferred',
			}),
			withViewTransitions({ skipInitialTransition: true })
		),
		provideAnimations(),
		provideWebPlatform(),
		provideRiskPlugins(ALL_RISK_PLUGINS, DEFAULT_PLUGIN),
		provideWebRiskUi(),
		provideQuoterRiskUi(),
		importProvidersFrom([
			JwtModule.forRoot({
				config: {
					// The access token now lives only in AuthService's in-memory signal
					// (cookies carry the session across reloads). JwtModule is kept solely
					// for JwtHelperService DI — every decode/expiry call now passes an
					// explicit token, so this getter is intentionally never read.
					tokenGetter: () => null,
				},
			}),
		]),
		provideAppInitializer(() => silentRefresh(inject(AuthService))),
		{ provide: LOCALE_ID, useValue: 'es-AR' },
		{ provide: IS_PROD, useValue: environment.production},
		{ provide: API_CONFIG, useValue: environment.apiUrl },
		{ provide: PAYMENTS_URL, useValue: environment.paymentsUrl },
		{ provide: AUTH_URL, useValue: environment.authUrl },
		{ provide: CHANNEL, useValue: CHANNEL_QUOTE_ENUM.AGENTS },
		{ provide: APP_VERSION, useValue: environment.version },
		{ provide: RECAPTCHA_SITE_KEY, useValue: environment.recaptchaSiteKey },
	],
};
